import React from 'react';
import { Box } from '@mui/material';
import { FormikProvider } from 'formik';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { useSupportRequestTypesQuery } from '@uplift/queries';
import { Button, Modal, ModalActions, ModalContent, Select, TextInput, useAlert } from '@uplift/ui-v2';
import { useSupportRequestFormHook } from './form.hook';


type SubmitRequestModalProps = {
  open: boolean;
  onClose: () => void;
};

export const SubmitRequestModal: React.VFC<SubmitRequestModalProps> = ({ open, onClose }) => {
  const l10n = useL10n();
  const me = useCurrentuser();
  const alert = useAlert();

  const { data: _requestTypes = [] } = useSupportRequestTypesQuery();
  const requestTypes = React.useMemo(() =>
    _requestTypes
      .filter(t => t.role === me?.role)
      .map(t => ({ value: t.id, label: t.name }))
  , [ _requestTypes, me?.role ]);

  const handleSuccess = () => {
    alert.success(l10n.support.submitSuccess.title);
    onClose();
  };

  const handleError = () => {
    alert.error(l10n.support.submitFail.desc);
  };

  const { formik, isFetching } = useSupportRequestFormHook(handleSuccess, handleError);

  const handleClose = () => {
    if (!isFetching) {
      formik.resetForm();
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={l10n.support.submitRequestTitle}
      fullWidth
      maxWidthPx="560px"
      loading={isFetching}
    >
      <FormikProvider value={formik}>
        <ModalContent>
          <Box display="flex" flexDirection="column" gap={3}>
            <Select
              label={l10n.support.selectTopic}
              name="supportTopic"
              items={requestTypes}
            />
            <TextInput
              multiline
              name="supportDetails"
              placeholder={l10n.support.placeholder}
              fullWidth
              label={l10n.support.moreDetails}
              rows={4}
            />
          </Box>
        </ModalContent>
        <ModalActions>
          <Button variant="outlined" onClick={handleClose} disabled={isFetching}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => formik.submitForm()}
            loading={isFetching}
            disabled={isFetching}
          >
            {l10n.support.submitRequestTitle}
          </Button>
        </ModalActions>
      </FormikProvider>
    </Modal>
  );
};
